import React from "react";
import { SortableHandle } from "react-sortable-hoc";
import { updateOrganizationIntegrationSettings } from "../../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import {
  BlueprintSpeed,
  OrganizationIntegrationSettings,
  PartnershipType,
  UserType,
} from "../../../../models/Entities";
import { spectrum } from "../../../../styles/theme";
import useAppContext from "../../../context/useAppContext";
import { showErrorToast } from "../../../shared/Toasts";
import { openSupportChat } from "../../../shared/utils/openSupportChat";
import {
  Badge,
  Button,
  ButtonVariant,
  Toggle,
  Tooltip,
  Text,
} from "@merge-api/merge-javascript-shared";
import { OrganizationAvatar, UserAvatarSize } from "../../../shared/MergeAvatars";
import { Check, AlertCircle, Menu } from "lucide-react";

const INTEGRATION_TOGGLE_ENABLED_ROLES = new Set<UserType>([
  UserType.admin_with_billing,
  UserType.admin_without_billing,
  UserType.member,
]);

interface Props {
  settings: OrganizationIntegrationSettings;
  onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
  isBeta: boolean;
  isToggleable: boolean;
}

/**
 * All possible colors for blueprint speeds
 */
export const BLUEPRINT_SPEED_COLORS: Record<BlueprintSpeed, string> = {
  SLOW: "#F69841",
  INTERMEDIATE: "#F5C15B",
  FAST: spectrum.teal50,
  CUSTOM: spectrum.gray50,
};

const DragHandle = SortableHandle(() => <Menu size={16} className="text-gray-40 cursor-grab" />);

/**
 * Creates the overview row for an integration on the settings page - one row of data, shown
 * whether it's collapsed or expanded
 */
const IntegrationSettingsRowOverview = ({
  settings,
  onIntegrationToggle,
  isBeta,
  isToggleable,
}: Props) => {
  const { user } = useAppContext();
  const canToggleIntegration = INTEGRATION_TOGGLE_ENABLED_ROLES.has(user.type);

  const { integration, organization_config_status: configStatusFields } = settings;
  const hasIncompleteFields = configStatusFields.some(
    (fieldConfigStatus) => !fieldConfigStatus.completed,
  );

  const isEnabled = settings.is_enabled ?? false;
  const hasMergeCreds = settings.has_merge_creds;
  const usingOrgCreds = settings.partnership_type === PartnershipType.ORGANIZATION_PARTNERSHIP;

  // Determine credential status for display
  const getCredentialStatus = () => {
    // 1. Using merge creds (have them AND actively using)
    if (hasMergeCreds && !usingOrgCreds) {
      return {
        text: "Merge credentials",
        icon: <Check size={12} className="text-gray-40" />,
        textClass: "text-gray-40",
        tooltip: "This integration is using Merge's default credentials",
      };
    }
    // 2. Missing credentials (no merge creds AND incomplete AND can configure)
    if (hasIncompleteFields && !hasMergeCreds && settings.partnership_available) {
      return {
        text: "Missing credentials",
        icon: <AlertCircle size={12} className="text-red-60" />,
        textClass: "text-gray-40",
        tooltip: "This integration will not appear in Merge Link until credentials are configured",
      };
    }
    // 3. Using org creds (complete AND using org creds)
    if (!hasIncompleteFields && usingOrgCreds) {
      return {
        text: "Your organization credentials",
        icon: <Check size={12} className="text-gray-40" />,
        textClass: "text-gray-40",
        tooltip: "This integration is using your organization's configured credentials",
      };
    }
    return null;
  };

  const credentialStatus = getCredentialStatus();

  const toggleIntegrationEnabled = (newIsEnabled: boolean) => {
    updateOrganizationIntegrationSettings({
      settingsId: settings.id,
      body: {
        is_enabled: newIsEnabled,
      },
      onUpdate: onIntegrationToggle,
      onError: (errorData) => {
        switch ((errorData && errorData.status) || -1) {
          case 409: {
            showErrorToast(
              `Cannot disable integration ${integration.name} because it has active Linked Accounts.`,
            );
            break;
          }
          case 403: {
            const toggle_action = newIsEnabled ? "enable" : "disable";
            showErrorToast(
              `Cannot ${toggle_action} integration ${integration.name} because you aren't an Admin.`,
            );
            break;
          }
          default: {
            showErrorToast("Failed to disable integration.");
            break;
          }
        }
      },
    });
  };

  return (
    <div className="flex flex-row items-center gap-x-4 w-full pr-6">
      {/* Drag Handle */}
      <Tooltip title="Reorder integrations to customize ordering in Merge Link">
        <DragHandle />
      </Tooltip>

      {/* Integration Logo */}
      <OrganizationAvatar
        size={UserAvatarSize.sm}
        imageURL={integration.square_image}
        organizationName={integration.name}
        isCircle
      />

      {/* Name and Credential Status */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Text variant="h6">{integration.name}</Text>
          {isBeta && <Badge color="blue">Beta</Badge>}
        </div>
        {isToggleable && credentialStatus && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <Tooltip title={credentialStatus.tooltip || undefined}>
              <span className="flex items-center justify-center">{credentialStatus.icon}</span>
            </Tooltip>
            <Text variant="caption" className={`${credentialStatus.textClass}`}>
              {credentialStatus.text}
            </Text>
          </div>
        )}
      </div>

      {/* Toggle or Request Access */}
      {!isToggleable ? (
        <Button
          variant={ButtonVariant.TertiaryWhite}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            openSupportChat();
          }}
        >
          Request access
        </Button>
      ) : (
        <Tooltip
          title={
            !canToggleIntegration
              ? "Insufficient permissions. Please contact your Merge administrator to access this feature."
              : undefined
          }
        >
          <div onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={isEnabled}
              disabled={!canToggleIntegration}
              label={isEnabled ? "Enabled" : "Disabled"}
              onChange={() => toggleIntegrationEnabled(!isEnabled)}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default IntegrationSettingsRowOverview;
